import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import FileUpload from "components/FileUpload/FileUpload";
import FileUploadsHistory from "components/FileUploadsHistory/FileUploadsHistory";
import {
  MAX_UPLOAD_FILE_ROWS,
  UPLOAD_ACTION_OPTIONS,
} from "constants/constants";
import useFileUpload from "hooks/useFileUpload";
import { SingleSelect } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./UploadsSection.module.scss";

const getActionFromScriptId = (scriptIdParam) => {
  if (!scriptIdParam) {
    return {};
  }

  const scriptId = Number(scriptIdParam);
  return UPLOAD_ACTION_OPTIONS.find((option) => option.scriptId === scriptId) ?? {};
};

const UploadsSection = () => {
  const [searchParams] = useSearchParams();
  const [selectedAction, setSelectedAction] = useState(() =>
    getActionFromScriptId(searchParams.get("scriptId"))
  );
  const fileInputRef = useRef(null);
  const { openSnackbar } = useSnackbarContext();

  const hasSelectedAction = Boolean(selectedAction?.scriptId);
  const { selectedFile, setSelectedFile, upload, isPending } = useFileUpload(
    selectedAction?.scriptId
  );

  const handleActionChange = (event) => {
    setSelectedAction(event.target.value);
    setSelectedFile(null);
  };

  const handleUploadClick = () => {
    upload(fileInputRef);
  };

  return (
    <div className={styles.container}>
      <p className={styles.pageSubtitle}>
        Upload your file and track processing history.
      </p>

      <div className={styles.uploadCard}>
        <div className={styles.actionField}>
          <label className={styles.fieldLabel} htmlFor="upload-action">
            Action <span className={styles.required}>*</span>
          </label>
          <div className={styles.actionInputRow}>
            <div className={styles.selectWrapper}>
              <SingleSelect
                menuItems={UPLOAD_ACTION_OPTIONS}
                value={selectedAction}
                placeholder="Select Action"
                handleChange={handleActionChange}
                hideNoneOption
                fullWidth
              />
            </div>
            <p className={styles.actionHint}>
              Choose the operation you want to perform.
            </p>
          </div>
        </div>

        <FileUpload
          selectedFile={selectedFile}
          onFileChange={setSelectedFile}
          onFileError={(message) => openSnackbar(message, "error")}
          disabled={!hasSelectedAction}
        />

        {hasSelectedAction && (
          <div className={styles.infoBannerSlot}>
            {selectedAction.infoText && (
              <div className={styles.infoBanner}>
                <InfoOutlinedIcon className={styles.infoIcon} />
                <span>{selectedAction.infoText}</span>
              </div>
            )}
          </div>
        )}

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Supported Format</span>
            <span className={styles.infoValue}>CSV Only (.csv)</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Max No. of Rows</span>
            <span className={styles.infoValue}>{MAX_UPLOAD_FILE_ROWS}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Header Row</span>
            <span className={styles.infoValue}>Mandatory</span>
          </div>
        </div>

        <div className={styles.cardFooter}>
          <button
            type="button"
            className={styles.uploadButton}
            disabled={!hasSelectedAction || !selectedFile || isPending}
            onClick={handleUploadClick}
          >
            <CloudUploadOutlinedIcon className={styles.uploadBtnIcon} />
            {isPending ? "Uploading..." : "Upload File"}
          </button>
        </div>
      </div>

      {hasSelectedAction && (
        <div className={styles.historyCard}>
          <FileUploadsHistory
            key={selectedAction.scriptId}
            scriptId={selectedAction.scriptId}
          />
        </div>
      )}
    </div>
  );
};

export default UploadsSection;
